import MembershipInfo from "@/components/hlidaci-pes/MembershipInfo";
import PromoCodeField from "@/components/hlidaci-pes/PromoCodeField";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { Alert } from "@/components/ui/Alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSessionContext } from "@/context/SessionContext";
import { Home } from "lucide-react";
import Link from "next/link";
import Script from "next/script";
import React, { useEffect } from "react";

const Membership = () => {
  const {
    isLoading,
    doPayment,
    handleSubmitPayment,
    selectedProduct,
    setSelectedProduct,
    registrationPromoCode,
    registrationPromoInfo,
    isPromoCodeChecking,
  } = useSessionContext();
  const { errorMessage, userInfoGlobal } = useAppContext();
  const { language } = useLanguage();
  const copy = {
    cs: {
      eyebrow: "Vyberte členství",
      title: "Dokončení registrace",
      level: "Úroveň členství",
      placeholder: "Vyberte svou úroveň členství",
      plans: {
        BASIC: "Základní",
        STANDARD: "Standardní",
        PREMIUM: "Premiové",
        PROFESSIONAL: "Profesionální",
      },
      termsPrefix: "Souhlasím s",
      terms: "obchodními podmínkami",
      paymentTerms: "platebními podmínkami a opakovanou platbou",
      loading: "Načítání",
      payment: "Pokračovat k platbě",
      continue: "Pokračovat",
      showMembership: "Zobrazit členství",
      showTerms: "Zobrazit VOP",
    },
    en: {
      eyebrow: "Choose membership",
      title: "Complete registration",
      level: "Membership level",
      placeholder: "Choose your membership level",
      plans: {
        BASIC: "Basic",
        STANDARD: "Standard",
        PREMIUM: "Premium",
        PROFESSIONAL: "Professional",
      },
      termsPrefix: "I agree with the",
      terms: "terms and conditions",
      paymentTerms: "payment terms and recurring payment",
      loading: "Loading",
      payment: "Continue to payment",
      continue: "Continue",
      showMembership: "Show membership",
      showTerms: "Show terms",
    },
  }[language];

  const [termsAgreementChecked, setTermsAgreementChecked] =
    React.useState(false);
  const [paymentTermsAgreementChecked, setPaymentTermsAgreementChecked] =
    React.useState(false);

  useEffect(() => {
    if (!selectedProduct) {
      setSelectedProduct(
        userInfoGlobal?.isEligibleForFreeTier ? "BASIC" : "PROFESSIONAL",
      );
    }
  }, [selectedProduct, setSelectedProduct, userInfoGlobal]);

  useEffect(() => {
    setPaymentTermsAgreementChecked(false);
  }, [doPayment]);

  const canProceed =
    selectedProduct !== "" &&
    !isPromoCodeChecking &&
    (!registrationPromoCode.trim() || registrationPromoInfo?.valid) &&
    termsAgreementChecked &&
    (!doPayment || paymentTermsAgreementChecked);
  const isMembershipFixedByPromo = Boolean(
    registrationPromoInfo?.valid && registrationPromoInfo.membership,
  );

  const membershipOptions = [
    {
      value: "BASIC",
      label: copy.plans.BASIC,
      disabled: !userInfoGlobal?.isEligibleForFreeTier,
    },
    {
      value: "STANDARD",
      label: copy.plans.STANDARD,
      disabled: false,
    },
    {
      value: "PREMIUM",
      label: copy.plans.PREMIUM,
      disabled: false,
    },
    {
      value: "PROFESSIONAL",
      label: copy.plans.PROFESSIONAL,
      disabled: false,
    },
  ];
  return (
    <>
      <Script
        src="https://gate.gopay.cz/gp-gw/js/embed.js"
        // src="https://gw.sandbox.gopay.com/gp-gw/js/embed.js"
        strategy="afterInteractive"
      />
      <Panel
        as="section"
        id="prihlaseni"
        variant="shell"
        className="p-5 sm:p-7"
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="app-block-eyebrow">{copy.eyebrow}</p>
            <h2 className="app-block-title mt-2">{copy.title}</h2>
          </div>
          <div className="rounded-full bg-softiq-primary p-3 text-white">
            <Home className="size-5" aria-hidden />
          </div>
        </div>

        <form
          className="space-y-4"
          onSubmit={handleSubmitPayment}
          id="payment-form"
        >
          <label className="app-form-field">
            Email
            <input
              type="email"
              name="email"
              autoComplete="email"
              className="h-12 rounded-lg border border-softiq-border bg-softiq-surface px-4 text-base font-normal text-softiq-copy outline-none transition focus:border-softiq-secondary focus:ring-2 focus:ring-softiq-secondary/15 dark:border-softiq-border-dark dark:bg-softiq-dark-surface dark:text-softiq-copy-inverse"
              value={userInfoGlobal?.email ?? ""}
              required
              readOnly
            />
          </label>

          <div className="app-form-field">
            <label htmlFor="watchDogLevel">{copy.level}</label>
            <Select
              name={isMembershipFixedByPromo ? undefined : "watchDogLevel"}
              value={selectedProduct ?? ""}
              onValueChange={(value) => {
                if (!isMembershipFixedByPromo) {
                  setSelectedProduct(value);
                }
              }}
              disabled={isMembershipFixedByPromo}
              required
            >
              <SelectTrigger
                id="watchDogLevel"
                className="h-12 rounded-lg border-softiq-border bg-softiq-surface px-4 text-base font-normal text-softiq-copy dark:border-softiq-border-dark dark:bg-softiq-dark-surface dark:text-softiq-copy-inverse"
                aria-label={copy.level}
              >
                <SelectValue placeholder={copy.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {membershipOptions.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isMembershipFixedByPromo ? (
              <input
                type="hidden"
                name="watchDogLevel"
                value={selectedProduct}
              />
            ) : null}
          </div>

          <MembershipInfo
            membership={selectedProduct}
            promoCodeInfo={registrationPromoInfo}
          />
          <PromoCodeField />

          <Panel variant="compact" className="space-y-3 p-4">
            <label className="flex cursor-pointer items-start gap-3 text-sm font-medium leading-5 text-softiq-copy-muted">
              <input
                type="checkbox"
                className="mt-0.5 size-4 rounded border-softiq-border"
                checked={termsAgreementChecked}
                onChange={(event) =>
                  setTermsAgreementChecked(event.target.checked)
                }
              />
              <span>
                {copy.termsPrefix}{" "}
                <Link
                  href="/obchodni-podminky"
                  className="font-semibold text-softiq-primary underline-offset-2 hover:underline dark:text-softiq-secondary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {copy.terms}
                </Link>
              </span>
            </label>

            {doPayment && (
              <label className="flex cursor-pointer items-start gap-3 text-sm font-medium leading-5 text-softiq-copy-muted">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 rounded border-softiq-border"
                  checked={paymentTermsAgreementChecked}
                  onChange={(event) =>
                    setPaymentTermsAgreementChecked(event.target.checked)
                  }
                />
                <span>
                  {copy.termsPrefix}{" "}
                  <Link
                    href="/podminky-predplatneho"
                    className="font-semibold text-softiq-primary underline-offset-2 hover:underline dark:text-softiq-secondary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {copy.paymentTerms}
                  </Link>
                </span>
              </label>
            )}
          </Panel>

          {errorMessage ? <Alert tone="danger">{errorMessage}</Alert> : null}

          <Button
            type="submit"
            variant="brand"
            size="form"
            loading={isLoading}
            loadingLabel={`${copy.loading}...`}
            disabled={!canProceed}
            className="w-full"
          >
            {doPayment ? copy.payment : copy.continue}
          </Button>
        </form>

        <div className="mt-5 grid gap-2 sm:grid-cols-2">
          <Button asChild variant="outline" size="form">
            <Link href="/#cenik">{copy.showMembership}</Link>
          </Button>
          <Button asChild variant="outline" size="form">
            <Link
              href="/obchodni-podminky"
              target="_blank"
              rel="noopener noreferrer"
            >
              {copy.showTerms}
            </Link>
          </Button>
        </div>
      </Panel>
    </>
  );
};

export default Membership;
